// client/src/components/configuracoes/disponibilidade/DisponibilidadePage.js
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '../../../services/api'; 
import FiltroDisponibilidade from './FiltroDisponibilidade';
import ResultadoDisponibilidade from './ResultadoDisponibilidade';
import Pagination from '../../common/Pagination';
import { useServicos } from '../../../hooks/useServicos';
import { useRecursos } from '../../../hooks/useRecursos';
import { useProfissionaisQuery } from '../../../hooks/useProfissionais'; 
import { useMyAccountDetails } from '../../../hooks/useAccounts';
import { useUnidadesPorConta } from '../../../hooks/useUnidades'; 
import { useDisponibilidade } from '../../../hooks/useDisponibilidade';
import useAlert from '../../../hooks/useAlert';
import { useMe } from '../../../hooks/useAuth';
import { Filter as FilterIcon, Loader2, XCircle, CalendarSearch } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';

const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item.$oid) return String(item.$oid);
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const getArrayData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.data && Array.isArray(data.data.data)) return data.data.data;
    return [];
};

const DisponibilidadePage = () => {
    const { setAlert } = useAlert();
    const { user } = useMe();
    const accountId = extractId(user?.account);

    const { data: servicosData, error: errorServicos } = useServicos({ limit: 1000, account: accountId });
    const { data: recursosData, error: errorRecursos } = useRecursos(accountId, { limit: 1000, status: 'Ativo' }); 
    const { data: profissionaisData, error: errorProfissionais } = useProfissionaisQuery({ accountId, status: 'ativo', limit: 1000 });
    const { data: accountDetails } = useMyAccountDetails();
    const { data: unidadesResponse, isLoading: loadingUnidades } = useUnidadesPorConta(accountId, { limit: 1000, ativo: true });

    const { data: userUnidadesResponse, isLoading: loadingUserUnidades } = useQuery({
        queryKey: ['user-unidades-disponibilidade', accountId],
        queryFn: async () => {
            try {
                const res = await api.get(`/relationships/user-unidades`, {
                    params: { account: accountId, status: 'ativo', limit: 2000 }
                });
                return res.data;
            } catch (err) {
                return { data: [] }; 
            }
        },
        enabled: !!accountId
    });

    const { data: userServicosResponse, isLoading: loadingUserServicos } = useQuery({
        queryKey: ['user-servicos-disponibilidade', accountId],
        queryFn: async () => {
            try {
                const res = await api.get(`/relationships/user-servicos`, {
                    params: { account: accountId, status: 'ativo', limit: 2000 }
                });
                return res.data;
            } catch (err) {
                return { data: [] }; 
            }
        },
        enabled: !!accountId
    });

    const userUnidades = getArrayData(userUnidadesResponse);
    const userServicos = getArrayData(userServicosResponse);
    const unidadesDaConta = getArrayData(unidadesResponse);
    const servicos = getArrayData(servicosData);
    
    const recursosComAgenda = useMemo(() => {
        const todosRecursos = getArrayData(recursosData);
        
        console.log('🔍 [DisponibilidadePage] Recursos recebidos:', todosRecursos.map(r => ({
            id: extractId(r),
            nome: r.nome,
            temAgendaPropria: r.temAgendaPropria,
            disponivelPara: r.disponivelPara,
            status: r.status,
            ativo: r.ativo,
            tipo: r.tipo
        })));
        
        const recursosFiltrados = todosRecursos.filter(recurso => {
            const isAtivo = recurso.status === 'Ativo' && recurso.ativo !== false;
            if (!isAtivo) return false;
            
            if (recurso.temAgendaPropria === true) return true;
            
            const permiteLocacao = recurso.disponivelPara?.some(dp => 
                dp === 'INTERNO_LOCACAO' || dp === 'EXTERNO_LOCACAO'
            );
            
            const permiteServicos = recurso.disponivelPara?.some(dp => 
                dp === 'INTERNO_SERVICOS' || dp === 'EXTERNO_SERVICOS'
            );
            
            return permiteLocacao || permiteServicos;
        });
        
        console.log('✅ [DisponibilidadePage] Recursos filtrados:', recursosFiltrados.map(r => r.nome));
        
        return recursosFiltrados;
    }, [recursosData]);

    const profissionaisMapped = useMemo(() => {
        return getArrayData(profissionaisData).map(p => ({
            _id: extractId(p.user), 
            nome: p.user?.name || p.user?.nome || p.name || 'Sem Nome'
        }));
    }, [profissionaisData]);

    const [showFilters, setShowFilters] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [resumoDaBusca, setResumoDaBusca] = useState(null);
    const [tipoBusca, setTipoBusca] = useState('servico');
    const [paramsBusca, setParamsBusca] = useState({});
    
    const [filtros, setFiltros] = useState({
        servicoId: '', 
        profissionalId: '', 
        recursoId: '',
        unidadeId: '', 
        dataInicio: new Date().toISOString().split('T')[0],
        dataFim: new Date().toISOString().split('T')[0],
        duracaoManual: '60',
        duracaoUnidade: 'minutos',
        finalidadeRecurso: 'INTERNO_LOCACAO'
    });

    const [filtrosDeVisualizacao, setFiltrosDeVisualizacao] = useState({ 
        livres: true, ocupados: false, insuficientes: false, bloqueados: true, intervalos: true
    });
    
    const itemsPerPage = 7;

    const { 
        data: diasRaw, 
        error, 
        isLoading, 
        isFetching 
    } = useDisponibilidade(paramsBusca, { 
        enabled: !!(
            paramsBusca.data && 
            paramsBusca.duracao && 
            paramsBusca.unidade && 
            (paramsBusca.profissional || paramsBusca.recursos || (paramsBusca.entidadeAgendavel && paramsBusca.tipoEntidade === 'Unidade')) 
        )
    });

    const listaDias = useMemo(() => getArrayData(diasRaw), [diasRaw]);

    useEffect(() => {
        if (errorServicos || errorRecursos || errorProfissionais) {
            setAlert('Aviso: Alguns dados complementares não foram carregados.', 'warning');
        }
    }, [errorServicos, errorRecursos, errorProfissionais, setAlert]);

    const handleVerificarDisponibilidade = (e) => {
        if (e) e.preventDefault();
        
        if (!filtros.unidadeId) { 
            setAlert('Por favor, selecione uma unidade de atendimento.', 'warning');
            return;
        }
        
        let params = { 
            data: filtros.dataInicio, 
            unidade: filtros.unidadeId 
        };
        let resumo = {};

        const getDuracaoManualEmMinutos = () => {
            const duracao = parseInt(filtros.duracaoManual, 10) || 0;
            return filtros.duracaoUnidade === 'horas' ? duracao * 60 : duracao;
        };

        try {
            if (tipoBusca === 'servico') {
                if (!filtros.servicoId) {
                    setAlert('Por favor, selecione um serviço.', 'warning'); return;
                }
                const servico = servicos.find(s => extractId(s) === filtros.servicoId);
                if (!servico) {
                    setAlert('O serviço selecionado não foi encontrado.', 'danger'); return;
                }
                
                const profissionalId = filtros.profissionalId;
                if (profissionalId) {
                    params.profissional = profissionalId;
                } else {
                    const linkFallback = userServicos.find(us => 
                        us.status === 'ativo' &&
                        extractId(us.servico) === filtros.servicoId && 
                        getArrayData(us.unidades).some(u => extractId(u) === filtros.unidadeId)
                    );
                    if (linkFallback) params.profissional = extractId(linkFallback.user);
                }
                
                const recursosIds = (servico.recursosVinculados || []).map(r => extractId(r)).filter(Boolean);
                if (recursosIds.length > 0) params.recursos = recursosIds.join(',');
                
                params.duracao = servico.duracaoSessaoEmMinutos || 60;
                params.tipoReserva = 'INTERNO_SERVICOS';
                resumo = { tipo: 'Serviço Interno', nome: servico.nome };

            } else if (tipoBusca === 'servicoExterno') {
                if (!filtros.profissionalId) {
                    setAlert('Para serviço externo, selecione um prestador externo.', 'warning'); return;
                }
                if (!filtros.servicoId) {
                    setAlert('Por favor, selecione um serviço.', 'warning'); return;
                }
                
                const servico = servicos.find(s => extractId(s) === filtros.servicoId);
                const profissional = profissionaisMapped.find(p => p._id === filtros.profissionalId);
                
                params.profissional = filtros.profissionalId;
                params.duracao = getDuracaoManualEmMinutos();
                params.tipoReserva = 'EXTERNO_SERVICOS';
                resumo = { tipo: 'Serviço Externo', nome: servico?.nome || 'Serviço', prestador: profissional?.nome || 'Prestador Externo' };

            } else if (tipoBusca === 'profissional') {
                if (!filtros.profissionalId) {
                    setAlert('Por favor, selecione um profissional.', 'warning'); return;
                }
                const prof = profissionaisMapped.find(p => p._id === filtros.profissionalId);
                
                params.profissional = filtros.profissionalId;
                params.duracao = getDuracaoManualEmMinutos();
                params.tipoReserva = 'INTERNO_SERVICOS';
                resumo = { tipo: 'Profissional', nome: prof?.nome || 'Profissional Selecionado' };

            } else if (tipoBusca === 'recurso') {
                if (!filtros.recursoId) {
                    setAlert('Por favor, selecione um recurso.', 'warning'); return;
                }
                const rec = recursosComAgenda.find(r => extractId(r) === filtros.recursoId);
                if (!rec) {
                    setAlert('O recurso selecionado não foi encontrado.', 'danger'); return;
                }

                params.recursos = filtros.recursoId;
                params.duracao = getDuracaoManualEmMinutos();
                params.tipoReserva = filtros.finalidadeRecurso;
                resumo = { 
                    tipo: filtros.finalidadeRecurso === 'EXTERNO_LOCACAO' ? 'Recurso (Locação Externa)' : 'Recurso (Locação Interna)', 
                    nome: rec.nome 
                };
            
            } else if (tipoBusca === 'recursoExterno') {
                if (!filtros.recursoId) {
                    setAlert('Por favor, selecione um recurso externo.', 'warning'); return;
                }
                const rec = recursosComAgenda.find(r => extractId(r) === filtros.recursoId);
                
                params.recursos = filtros.recursoId;
                params.duracao = getDuracaoManualEmMinutos();
                params.tipoReserva = 'EXTERNO_LOCACAO';
                resumo = { tipo: 'Recurso (Locação Externa)', nome: rec?.nome || 'Recurso Externo' };

            } else if (tipoBusca === 'unidade') { 
                const uni = unidadesDaConta.find(u => extractId(u) === filtros.unidadeId); 
                if (!uni) {
                    setAlert('A unidade selecionada não foi encontrada.', 'danger'); return;
                }
                params.entidadeAgendavel = filtros.unidadeId; 
                params.tipoEntidade = 'Unidade'; 
                params.duracao = getDuracaoManualEmMinutos();
                params.tipoReserva = 'INTERNO_SERVICOS';
                resumo = { tipo: 'Unidade', nome: uni.nome }; 
            }
            
            setResumoDaBusca(resumo);
            setShowFilters(false);
            setCurrentPage(1);
            setParamsBusca(params);

        } catch (err) {
            console.error('Erro ao verificar disponibilidade:', err);
            setAlert('Ocorreu um erro inesperado ao verificar disponibilidade.', 'danger');
        }
    };
    
    const handleFiltroVisualizacaoChange = (e) => {
        setFiltrosDeVisualizacao({ ...filtrosDeVisualizacao, [e.target.name]: e.target.checked });
    };

    const paginatedResults = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return listaDias.slice(startIndex, startIndex + itemsPerPage);
    }, [currentPage, itemsPerPage, listaDias]);

    return (
        <div className="page-container">
            {/* Antes montava o cabecalho na mao (page-header/page-title
                direto), sem o dropdown ⋮ que o PageHeader compartilhado
                ja da de graca no mobile */}
            <PageHeader
                icon={<CalendarSearch />}
                title="Verificar Disponibilidade"
                actions={
                    <button type="button" onClick={() => setShowFilters(prev => !prev)} className="action-button action-button-secondary">
                        <FilterIcon size={18} className="icon"/> {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
                    </button>
                }
            />

            {showFilters && (
                <FiltroDisponibilidade 
                    filtros={filtros}
                    setFiltros={setFiltros}
                    tipoBusca={tipoBusca}
                    setTipoBusca={setTipoBusca}
                    onSubmit={handleVerificarDisponibilidade} 
                    loading={isFetching || loadingUnidades || loadingUserServicos || loadingUserUnidades}
                    servicos={servicos}
                    profissionais={profissionaisMapped}
                    recursosComAgenda={recursosComAgenda}
                    unidadesDaConta={unidadesDaConta}
                    userServicos={userServicos} 
                    userUnidades={userUnidades}
                />
            )}
            
            {resumoDaBusca && !showFilters && !isLoading && (
                <div className="search-summary-panel">
                    Mostrando disponibilidade para <strong>{resumoDaBusca.tipo}:</strong>
                    <span className="summary-value">{resumoDaBusca.nome}</span>
                    {resumoDaBusca.prestador && (
                        <span className="summary-value"> - Prestador: {resumoDaBusca.prestador}</span>
                    )}
                </div>
            )}
            
            {isFetching && (
                <div className="content-panel mt-6">
                    <div className="panel-state-wrapper">
                        <Loader2 size={32} className="animate-spin icon text-primary" />
                        <span>Buscando horários...</span>
                    </div>
                </div>
            )}

            {error && !isFetching && (
                <div className="content-panel mt-6">
                    <div className="panel-state-wrapper panel-error">
                        <XCircle size={32} className="icon"/>
                        <span>{error.message || 'Erro ao processar a requisição de disponibilidade'}</span>
                    </div>
                </div>
            )}

            {!isFetching && !error && (
                <div className="mt-6">
                    {listaDias.length > 0 ? (
                        <>
                            <div className="content-panel mb-4">
                                <div className="panel-filters-bar">
                                    <h4 className="panel-subtitle">Exibir horários:</h4>
                                    <div className="form-group-checkbox">
                                        <input type="checkbox" id="ver-livres" name="livres" checked={filtrosDeVisualizacao.livres} onChange={handleFiltroVisualizacaoChange} />
                                        <label htmlFor="ver-livres">Livres</label>
                                    </div>
                                    <div className="form-group-checkbox">
                                        <input type="checkbox" id="ver-ocupados" name="ocupados" checked={filtrosDeVisualizacao.ocupados} onChange={handleFiltroVisualizacaoChange} />
                                        <label htmlFor="ver-ocupados">Ocupados</label>
                                    </div>
                                    <div className="form-group-checkbox">
                                        <input type="checkbox" id="ver-insuficientes" name="insuficientes" checked={filtrosDeVisualizacao.insuficientes} onChange={handleFiltroVisualizacaoChange} />
                                        <label htmlFor="ver-insuficientes">Insuficientes</label>
                                    </div>
                                    <div className="form-group-checkbox">
                                        <input type="checkbox" id="ver-intervalos" name="intervalos" checked={filtrosDeVisualizacao.intervalos} onChange={handleFiltroVisualizacaoChange} />
                                        <label htmlFor="ver-intervalos">Intervalos</label>
                                    </div>
                                    <div className="form-group-checkbox">
                                        <input type="checkbox" id="ver-bloqueados" name="bloqueados" checked={filtrosDeVisualizacao.bloqueados} onChange={handleFiltroVisualizacaoChange} />
                                        <label htmlFor="ver-bloqueados">Bloqueados</label>
                                    </div>
                                </div>
                            </div>
                            
                            <ResultadoDisponibilidade 
                                dias={paginatedResults} 
                                isSingleDay={paginatedResults.length === 1} 
                                filtrosDeVisualizacao={filtrosDeVisualizacao}
                                resumoDaBusca={resumoDaBusca}
                                dataDaBusca={paramsBusca.data}
                            />
                            
                            {listaDias.length > itemsPerPage && (
                                <Pagination 
                                    totalItems={listaDias.length} 
                                    itemsPerPage={itemsPerPage} 
                                    onPageChange={setCurrentPage} 
                                    currentPage={currentPage} 
                                />
                            )}
                        </>
                    ) : (
                        <div className="content-panel mt-6">
                            <div className="panel-state-wrapper">
                                <span>Nenhum horário disponível para os critérios selecionados.</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DisponibilidadePage;